/**
 * Keeping a WebSocket alive, and noticing when it is dead although it looks open.
 *
 * Every {@link KEEPALIVE_MS} the page sends a `system.ping` (id 0: no request waits for its
 * answer, so the client drops it). That traffic stops proxies (Cloudflare closes a quiet
 * WebSocket after about 100 s) from cutting the connection, and the answer proves it works:
 * when a ping was not answered by the next one, the socket is dead (a computer woke up, the
 * network changed) and is dropped, so the page reconnects instead of waiting for ever.
 *
 * Checked against the previous ping, not a clock: a hidden tab whose timers the browser slows
 * down to once a minute is not taken for dead.
 */

/** Time between two keepalive pings (ms). */
export const KEEPALIVE_MS = 25_000;

/** The frame a keepalive sends: a ping no request waits for. */
export const KEEPALIVE_FRAME = JSON.stringify({ id: 0, method: "system.ping", params: { message: "keepalive" } });

/** One connection's keepalive. */
export interface Keepalive {
  /** Something arrived on the connection: it is alive. */
  heard(): void;
  /** The timer fired: drops the connection if the last ping went unanswered, else pings again. */
  tick(): void;
}

/** A keepalive that sends with `send` and calls `drop` once the connection is found dead. */
export function createKeepalive(send: (frame: string) => void, drop: () => void): Keepalive {
  /** Whether a ping is out and nothing came back since. */
  let waiting = false;
  return {
    heard() {
      waiting = false;
    },
    tick() {
      if (waiting) return drop();
      waiting = true;
      send(KEEPALIVE_FRAME);
    },
  };
}
